/**
 * Referrer registry — who gets the credit for a signup.
 *
 * Source of truth for the `referrers` collection. One document per person who
 * can be credited with bringing someone to Melio: almost always a member of
 * staff, occasionally a partner practice or an existing member.
 *
 * WHY A REGISTRY AND NOT JUST FREE TEXT. The waitlist asks "did someone tell
 * you about us?" and the honest answer is a name, not a code — so that is what
 * the box takes. But a bonus cannot be paid off "Jon" / "jonathan" / "Jonathan
 * Davies" / "jon d", which is what a free-text field actually collects. This
 * collection is the join table that turns those into one id. Anything it fails
 * to resolve is kept verbatim on the entry and shown to ops for a manual
 * attribution, so the miss is visible rather than silently uncredited.
 *
 * WHY NOT `acquisition.referrerUid`. That field is MEMBER-to-member referral,
 * set by the `/invite/:code` path and scoped to first-touch attribution. This
 * is STAFF attribution, it is self-reported at the point of joining, and it
 * has to survive someone hearing about Melio at a dinner party with no link to
 * click. Overloading one field with both would make neither answerable.
 *
 * PRIVACY: a referrer record is about an employee, not a patient. Nothing
 * clinical, and no member data. The raw text a visitor types IS personal data
 * about a third party — cap it, never render it as HTML, and include it in the
 * entry's export / erasure scope like every other waitlist field.
 */

import type { Timestamp } from 'firebase/firestore';

export type ReferrerKind = 'staff' | 'clinician' | 'partner' | 'member';

export const REFERRER_KIND_LABELS: Record<ReferrerKind, string> = {
  staff: 'Team',
  clinician: 'Clinician',
  partner: 'Partner',
  member: 'Member',
};

/**
 * Document id is the CODE itself, uppercased. That makes the code path a
 * single `get()` with no index and no query, and makes uniqueness a property
 * of Firestore rather than something a seed script has to police.
 */
export interface ReferrerDoc {
  /** Mirrors the document id. Uppercase, e.g. "JONATHAN" or "KINGS-CROSS". */
  code: string;
  /** Shown to ops, and to the visitor when they arrive on a `?ref=` link. */
  displayName: string;
  kind: ReferrerKind;
  /** `staff_accounts/{uid}` when this is an employee; null for partners. */
  staffUid: string | null;
  /**
   * Normalised strings that resolve to this referrer — the code itself plus
   * every name variant a visitor might plausibly type. Queried with
   * array-contains, so no composite index is required.
   *
   * Build it with buildReferrerLookupKeys, never by hand: the resolver and
   * this list must normalise identically or a name that looks right will not
   * match.
   */
  lookupKeys: string[];
  /** Inactive referrers stop matching but keep their historic attributions. */
  active: boolean;
  notes?: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * 2–24 characters, letters/digits/hyphen, starting on a letter or digit.
 * Deliberately readable: codes go in links people paste into WhatsApp, and a
 * random slug would make `?ref=` unverifiable at a glance.
 */
export const REFERRER_CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]{1,23}$/;

/** Hard cap on the free text we store from the box. */
export const MAX_REFERRAL_RAW_LENGTH = 80;

/** Strip diacritics so "Siân" and "Sian" are the same person. */
function stripDiacritics(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Normalise anything a human typed into a comparison key: lowercase,
 * unaccented, punctuation collapsed to single spaces.
 *
 * MUST stay byte-identical in behaviour to `_normalise_referral_key` in
 * python_functions/main.py. The two run on opposite sides of the callable and
 * a divergence shows up as a name that mysteriously will not match.
 */
export function normaliseReferralKey(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const key = stripDiacritics(String(raw))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return key ? key.slice(0, MAX_REFERRAL_RAW_LENGTH) : null;
}

/**
 * Read input as a referrer CODE, or null if it is not code-shaped.
 *
 * Anything containing whitespace is rejected outright rather than having its
 * spaces squeezed out: "Jonathan Davies" must not silently become the code
 * JONATHANDAVIES and match someone by accident. Names go down the lookup-key
 * path instead.
 */
export function normaliseReferrerCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed || /\s/.test(trimmed)) return null;
  const code = stripDiacritics(trimmed).toUpperCase().replace(/[^A-Z0-9-]/g, '');
  return REFERRER_CODE_PATTERN.test(code) ? code : null;
}

/**
 * The lookup keys for a referrer: the code, the display name, and whatever
 * aliases were supplied. Duplicates and empties are dropped.
 *
 * First names are NOT derived automatically. Two Jonathans would then both
 * claim "jonathan", and the resolver's ambiguity guard would quietly refuse to
 * credit either of them — which looks exactly like the feature not working.
 * Adding a first name is a decision someone makes per referrer.
 */
export function buildReferrerLookupKeys(
  code: string,
  displayName: string,
  aliases: string[] = [],
): string[] {
  const keys = [code, displayName, ...aliases]
    .map((value) => normaliseReferralKey(value))
    .filter((value): value is string => Boolean(value));
  return Array.from(new Set(keys));
}
